import {createTheme} from "@mui/material/styles"
import {containerQuery} from "./container-query"
import {deDE} from "@mui/x-date-pickers/locales"
import "@fontsource/urbanist/100"
import "@fontsource/urbanist/200"
import "@fontsource/urbanist/300"
import "@fontsource/urbanist/400"
import "@fontsource/urbanist/500"
import "@fontsource/urbanist/600"
import "@fontsource/urbanist/700"
import "@fontsource/urbanist/800"
import "@fontsource/urbanist/900"
import {ChevronDown} from "lucide-react"

export const tpBlue = "#0071c2"

export const fontSize = {
    50: "10px",
    100: "12px",
    200: "14px",
    300: "16px",
    400: "18px",
    500: "20px",
    600: "24px",
    700: "32px",
}

export const fontWeight = {
    thin: 100,
    light: 200,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
}

export const themeGenerator = (designtoken) =>
    createTheme(
        {
            table: {
                primaryColor: "#E5EAFA",
                secondaryColor: "#FFFFFF",
            },

            typography: {
                fontFamily: designtoken?.fonts?.primaryFont
                    ? `"${designtoken.fonts.primaryFont}", Urbanist, Arial, sans-serif`
                    : "Urbanist, Arial, sans-serif",
                htmlFontSize: 16,
                h1: {
                    fontSize: "clamp(2rem, 4.5vw + 1rem, 60px)",
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: "-0.6px",
                    hyphens: "auto",
                },
                h2: {
                    fontSize: "20px",
                    fontWeight: "700",
                    lineHeight: "1.75",
                    color: "rgb(25, 34, 42)",
                },
                h3: {
                    fontSize: "18px",
                    lineHeight: "1.75",
                    color: "rgb(25, 34, 42)",
                    fontWeight: 400,
                },
                h4: {
                    fontSize: "16px",
                    lineHeight: "1.75",
                    color: "rgb(25, 34, 42)",
                    fontWeight: 600,
                },
                h5: {
                    fontSize: "24px",
                    lineHeight: "28px",
                    color: "rgb(0, 0, 0)",
                    fontWeight: 700,
                },
                h6: {
                    fontSize: "20px",
                    lineHeight: "24px",
                    color: "rgb(0, 0, 0)",
                    fontWeight: 700,
                },
                subtitle1: {
                    fontSize: 20,
                    color: designtoken.typography.subtitle1Color,
                },
                subtitle2: {
                    fontSize: 16,
                    fontWeight: 800,
                },
                body1: {
                    color: "#000",
                    fontSize: "15px",
                    lineHeight: "24px",
                    textAlign: "left",
                    fontWeight: 500,
                },
                body2: {
                    fontSize: "12px",
                    fontWeight: "500",
                },
                body3: {
                    fontSize: "12px",
                    fontWeight: "200",
                },
                button: {
                    fontSize: 13,
                    fontWeight: 800,
                },
                caption: {
                    fontSize: 11.1,
                    fontWeight: 800,
                },
                overline: {
                    fontSize: 11.1,
                    fontWeight: 800,
                },
                label: {
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: "black",
                },
            },

            spacing: 8,

            components: {
                StyledAlertCard: {
                    styleOverrides: {
                        root: {
                            padding: "16px",
                            borderRadius: "8px",
                            border: "1px solid transparent",
                        },
                    },
                    variants: [
                        {
                            props: {variant: "info"},
                            style: {
                                backgroundColor: designtoken?.AlertCard?.info?.backgroundColor,
                                borderColor: designtoken?.AlertCard?.info?.borderColor,
                                subtitleColor: designtoken?.AlertCard?.info?.subtitleColor,
                                "& .AlertCardDot": {
                                    backgroundColor: designtoken?.AlertCard?.info?.dotColor,
                                },
                            },
                        },
                        {
                            props: {variant: "warning"},
                            style: {
                                backgroundColor: designtoken?.AlertCard?.warning?.backgroundColor,
                                borderColor: designtoken?.AlertCard?.warning?.borderColor,
                                subtitleColor: designtoken?.AlertCard?.warning?.subtitleColor,
                                "& .AlertCardDot": {
                                    backgroundColor: designtoken?.AlertCard?.warning?.dotColor,
                                },
                            },
                        },
                        {
                            props: {variant: "success"},
                            style: {
                                backgroundColor: designtoken?.AlertCard?.success?.backgroundColor,
                                borderColor: designtoken?.AlertCard?.success?.borderColor,
                                subtitleColor: designtoken?.AlertCard?.success?.subtitleColor,
                                "& .AlertCardDot": {
                                    backgroundColor: designtoken?.AlertCard?.success?.dotColor,
                                },
                            },
                        },
                        {
                            props: {error: true},
                            style: {
                                backgroundColor: designtoken?.AlertCard?.error?.backgroundColor,
                                borderColor: designtoken?.AlertCard?.error?.borderColor,
                                subtitleColor: designtoken?.AlertCard?.error?.subtitleColor,
                                "& .AlertCardDot": {
                                    backgroundColor: designtoken?.AlertCard?.error?.dotColor,
                                },
                            },
                        },
                        {
                            props: {disabled: true},
                            style: {
                                backgroundColor: designtoken?.AlertCard?.disabled?.backgroundColor,
                                borderColor: designtoken?.AlertCard?.disabled?.borderColor,
                                subtitleColor: designtoken?.AlertCard?.disabled?.subtitleColor,
                                "& .AlertCardDot": {
                                    backgroundColor: designtoken?.AlertCard?.disabled?.dotColor,
                                },
                            },
                        },
                    ],
                },
                AlertCardTitle: {
                    variants: [
                        {props: {variant: "info"},
                            style: {
                                fontSize: designtoken?.AlertCard?.info?.titleFontSize,
                                lineHeight: designtoken?.AlertCard?.info?.titleLineHeight,
                                color: designtoken?.AlertCard?.info?.titleColor,
                                fontWeight: designtoken?.AlertCard?.info?.titleFontWeight,
                            },
                        },
                        {props: {variant: "warning"},
                            style: {
                                fontSize: designtoken?.AlertCard?.warning?.titleFontSize,
                                lineHeight: designtoken?.AlertCard?.warning?.titleLineHeight,
                                color: designtoken?.AlertCard?.warning?.titleColor,
                                fontWeight: designtoken?.AlertCard?.warning?.titleFontWeight,
                            },
                        },
                        {props: {variant: "success"},
                            style: {
                                fontSize: designtoken?.AlertCard?.success?.titleFontSize,
                                lineHeight: designtoken?.AlertCard?.success?.titleLineHeight,
                                color: designtoken?.AlertCard?.success?.titleColor,
                                fontWeight: designtoken?.AlertCard?.success?.titleFontWeight,
                            },
                        },
                        {props: {error: true},
                            style: {
                                fontSize: designtoken?.AlertCard?.error?.titleFontSize,
                                lineHeight: designtoken?.AlertCard?.error?.titleLineHeight,
                                color: designtoken?.AlertCard?.error?.titleColor,
                                fontWeight: designtoken?.AlertCard?.error?.titleFontWeight,
                            },
                        },
                        {props: {disabled: true},
                            style: {
                                fontSize: designtoken?.AlertCard?.disabled?.titleFontSize,
                                lineHeight: designtoken?.AlertCard?.disabled?.titleLineHeight,
                                color: designtoken?.AlertCard?.disabled?.titleColor,
                                fontWeight: designtoken?.AlertCard?.disabled?.titleFontWeight,
                            },
                        },
                    ],
                },
                AlertCardSubtitle: {
                    variants: [
                        {props: {variant: "info"},
                            style: {
                                fontSize: designtoken?.AlertCard?.info?.subtitleFontSize,
                                lineHeight: designtoken?.AlertCard?.info?.subtitleLineHeight,
                                color: designtoken?.AlertCard?.info?.subtitleColor,
                                fontWeight: designtoken?.AlertCard?.info?.subtitleFontWeight,
                            },
                        },
                        {props: {variant: "warning"},
                            style: {
                                fontSize: designtoken?.AlertCard?.warning?.subtitleFontSize,
                                lineHeight: designtoken?.AlertCard?.warning?.subtitleLineHeight,
                                color: designtoken?.AlertCard?.warning?.subtitleColor,
                                fontWeight: designtoken?.AlertCard?.warning?.subtitleFontWeight,
                            },
                        },
                        {props: {variant: "success"},
                            style: {
                                fontSize: designtoken?.AlertCard?.success?.subtitleFontSize,
                                lineHeight: designtoken?.AlertCard?.success?.subtitleLineHeight,
                                color: designtoken?.AlertCard?.success?.subtitleColor,
                                fontWeight: designtoken?.AlertCard?.success?.subtitleFontWeight,
                            },
                        },
                        {props: {error: true},
                            style: {
                                fontSize: designtoken?.AlertCard?.error?.subtitleFontSize,
                                lineHeight: designtoken?.AlertCard?.error?.subtitleLineHeight,
                                color: designtoken?.AlertCard?.error?.subtitleColor,
                                fontWeight: designtoken?.AlertCard?.error?.subtitleFontWeight,
                            },
                        },
                        {props: {disabled: true},
                            style: {
                                fontSize: designtoken?.AlertCard?.disabled?.subtitleFontSize,
                                lineHeight: designtoken?.AlertCard?.disabled?.subtitleLineHeight,
                                color: designtoken?.AlertCard?.disabled?.subtitleColor,
                                fontWeight: designtoken?.AlertCard?.disabled?.subtitleFontWeight,
                            },
                        },
                    ],
                },
                MuiTabs: {
                    styleOverrides: {
                        indicator: {
                            backgroundColor: designtoken?.tabs?.indicatorColor,
                        },
                    },
                },
                MuiTab: {
                    styleOverrides: {
                        root: {
                            textTransform: "none",
                            fontSize: "18px",
                            padding: "16px 0",
                            margin: "0 16px",
                            minWidth: "0",
                            "&:first-of-type": {
                                marginLeft: "0px",
                            },
                            "&:last-of-type": {
                                marginRight: "0px",
                            },
                            "&.Mui-selected": {
                                color: designtoken?.tabs?.selectedColor,
                            },
                        },
                    },
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            "& .MuiInputBase-root": {height: "100%"},
                            rows: 6,
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            textTransform: "none",
                            paddingLeft: "32px",
                            paddingRight: "32px",
                            fontSize: "16px",
                            borderRadius: "8px",
                            fontStyle: "normal",
                            fontWeight: 500,
                        },
                        sizeSmall: {
                            height: "36px",
                        },
                        sizeMedium: {
                            height: "44px",
                        },
                    },
                    variants: [
                        {
                            props: {variant: "primary"},
                            style: {
                                backgroundColor:
                                designtoken.button.primary.backgroundColor,
                                width: "100%",
                                borderRadius:
                                designtoken.button.primary.borderRadius,
                                color: designtoken.button.primary.color,
                                fontStyle:
                                    "fonts/urbanist/fontFamily/Urbanist",

                                "&:hover": {
                                    backgroundColor:
                                    designtoken.button.primary
                                        .backgroundColorHover,
                                },
                                "&.Mui-focusVisible": {
                                    boxShadow:
                                    designtoken.button.primary.boxShadow,
                                    backgroundColor:
                                    designtoken.button.primary
                                        .backgroundColorFocus,
                                },
                                "&.Mui-disabled": {
                                    backgroundColor:
                                    designtoken.button.secondary
                                        .backgroundDisabled,
                                    color: designtoken.button.secondary
                                        .colorDisabled,
                                },
                                "&.MuiButton-colorError": {
                                    backgroundColor:
                                    designtoken.button.primary
                                        .errorBackgroundColor,
                                    color: designtoken.button.primary
                                        .errorColor,
                                    "& .MuiSvgIcon-root": {
                                        color: designtoken.button.primary
                                            .errorColor,
                                    },
                                },
                                "&:active": {
                                    backgroundColor:
                                    designtoken.button.primary
                                        .backgroundActive,
                                },
                            },
                        },
                        {
                            props: {variant: "secondary"},
                            style: {
                                color: designtoken.button.secondary.color,
                                width: "100%",
                                backgroundColor:
                                designtoken.button.secondary
                                    .backgroundColor,
                                "&:hover": {
                                    color: designtoken.button.secondary
                                        .colorHover,
                                    backgroundColor:
                                    designtoken.button.secondary
                                        .backgroundColorHover,
                                },
                                "&.Mui-focusVisible": {
                                    boxShadow:
                                    designtoken.button.secondary.boxShadow,
                                    borderRadius: "5px",
                                },
                                "&.Mui-disabled": {
                                    backgroundColor:
                                    designtoken.button.secondary
                                        .backgroundDisabled,
                                    color: designtoken.button.secondary
                                        .colorDisabled,
                                },
                                "&:active": {
                                    backgroundColor:
                                    designtoken.button.secondary
                                        .backgroundActive,
                                },
                            },
                        },

                        {
                            props: {variant: "tertiary"},
                            style: {
                                width: "100%",
                                border: "1px solid",
                                color: designtoken.button.tertiary.color,
                                borderColor:
                                designtoken.button.tertiary.borderColor,
                                backgroundColor:
                                designtoken.button.tertiary.backgroundColor,
                                "&:hover": {
                                    color: designtoken.button.tertiary
                                        .colorHover,
                                    backgroundColor:
                                    designtoken.button.tertiary
                                        .backgroundColorHover,
                                },
                                "&:active": {
                                    backgroundColor: designtoken.button.tertiary
                                        .backgroundColorFocus,
                                    color: designtoken.button.tertiary.colorFocus,
                                },
                                "&.Mui-focusVisible": {
                                    border: designtoken.button.tertiary
                                        .borderFocus,
                                    borderRadius: "5px",
                                    backgroundColor: designtoken.button.tertiary
                                        .backgroundColorFocus,
                                    color: designtoken.button.tertiary.colorFocus,
                                },
                                "&.Mui-disabled": {
                                    backgroundColor:
                                    designtoken.button.secondary
                                        .backgroundDisabled,
                                    color: designtoken.button.secondary
                                        .colorDisabled,
                                },
                            },
                        },
                        {
                            props: {variant: "ghost"},
                            style: {
                                color: designtoken.button.ghost.color,
                                width: "100%",
                                backgroundColor:
                                designtoken.button.ghost.backgroundColor,
                                padding: "24px",
                                borderRadius:
                                designtoken.button.ghost.borderRadius,
                                border: designtoken.button.ghost.border,
                                outline: designtoken.button.ghost.outline,
                                "box-shadow":
                                designtoken.button.ghost.boxShadow,

                                "&:hover": {
                                    backgroundColor:
                                        designtoken.button.ghost
                                            .backgroundColorHover ||
                                        designtoken.button.tertiary
                                            .backgroundColorHover,
                                    color: designtoken.button.ghost.colorHover,
                                },
                                "&.Mui-focusVisible": {
                                    boxShadow:
                                    designtoken.button.ghost.focusBoxShadow,
                                    borderRadius: "5px",
                                },
                                "&:active": {
                                    backgroundColor: designtoken.button.ghost.backgroundColorActive,
                                    color: designtoken.button.ghost.colorActive,
                                },
                                "&.Mui-disabled": {
                                    backgroundColor:
                                        designtoken.button.ghost
                                            .backgroundDisabled ||
                                        designtoken.button.secondary
                                            .backgroundDisabled,
                                    color:
                                        designtoken.button.ghost.colorDisabled ||
                                        designtoken.button.secondary
                                            .colorDisabled,
                                },
                            },
                        },
                    ],
                },

                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontFamily: "Plus Jakarta Sans Variable,sans-serif",
                        borderRadius: "8px",
                    },
                },

                MuiIconButton: {
                    styleOverrides: {
                        root: {
                            color: designtoken.button.tertiary.color,
                            borderColor: designtoken.button.tertiary.borderColor,
                            backgroundColor:
                            designtoken.button.tertiary.backgroundColor,
                            "&:hover": {
                                color: designtoken.button.tertiary.colorHover,
                                backgroundColor:
                                designtoken.button.tertiary
                                    .backgroundColorHover,
                            },
                            "&:active": {
                                backgroundColor: "#7EB5C9",
                            },
                            "&.Mui-focusVisible": {
                                border: designtoken.button.tertiary.borderFocus,
                                borderRadius: "5px",
                            },
                            "&.Mui-disabled": {
                                backgroundColor:
                                designtoken.button.secondary.backgroundDisabled,
                                color: designtoken.button.secondary.colorDisabled,
                            },
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            boxShadow: "none",
                        },
                    },
                },
                MuiFormControl: {
                    defaultProps: {
                        variant: "outlined",
                    },
                    styleOverrides: {
                        root: {
                            width: "100%",
                        },
                    },
                },
                MuiSelect: {
                    defaultProps: {
                        IconComponent: ChevronDown,
                    },
                    styleOverrides: {
                        icon: {
                            color: "#19222A",
                            top: "5px",
                            bottom: "5px",
                            right: "0px",
                            height: "38px",
                            borderLeft: "1px solid #19222A",
                            width: "46px",
                            padding: "8px",
                        },
                        iconOpen: {
                            borderRight: "1px solid #19222A",
                            borderLeft: "none",
                        },
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        root: {
                            fullWidth: true,
                            backgroundColor:
                            designtoken.inputField.default.backgroundColor,
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            padding: "12px",
                            fontSize: 16,
                            fontWeight: 400,
                            height: "47px",
                            color: "#000000",
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: "1px solid",
                                borderColor: designtoken.inputField.borderColor,
                                borderRadius:
                                designtoken.inputField.borderRadius,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor:
                                designtoken.inputField.hoverBorderColor,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                boxShadow: `0 0 0 1px white, 0 0 0 3px ${designtoken.inputField.focusedBoxShadowColor}`,
                                borderRadius:
                                designtoken.inputField.default.borderRadius,
                                borderWidth:
                                designtoken.inputField.default
                                    .focusedBorderWidth,
                                borderColor:
                                designtoken.inputField.focusedBorderColor,
                            },
                            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                                borderColor:
                                designtoken.inputField.default
                                    .disabledBorderColor,
                                backgroundColor:
                                designtoken.inputField
                                    .disabledBackgroundColor,
                            },
                            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                                borderColor: designtoken.inputField?.errorBorderColor || "#D62728",
                            },
                            [`& .MuiInputBase-input `]: {
                                padding: 0,
                                "&:hover fieldset": {},
                            },
                            [`& .MuiButtonBase-root`]: {
                                padding: "0 12px 0 0",
                            },
                        },
                    },
                },
                MuiFilledInput: {
                    defaultProps: {
                        disableUnderline: true,
                    },
                    styleOverrides: {
                        input: {
                            paddingLeft: 20,
                        },
                        root: {
                            fontSize: 16,
                            fontWeight: 400,
                            height: "60px",
                            border: "2px solid #ff69b4",
                            backgroundColor: "#FFFFFF",
                            borderRadius: 4,
                            // "&:hover, &:active, &.Mui-focused":
                            //     {
                            //         backgroundColor: "white",
                            //         borderColor:
                            //             "#ff69b4",
                            //     },
                        },
                    },
                },
                MuiCheckbox: {
                    styleOverrides: {
                        root: {
                            "&:hover": {
                                backgroundColor: "inherit",
                            },
                            "& svg": {
                                fill: designtoken.checkbox.defaultBorderColor,
                            },
                            "&:hover svg": {
                                fill: designtoken.checkbox.onHover,
                            },
                            "&.Mui-disabled svg": {
                                strokeWidth: "1px solid",
                                fill: designtoken.checkbox.disabled,
                            },
                            "&.Mui-focusVisible svg": {
                                fill: designtoken.checkbox.activeColor,
                                strokeWidth: designtoken.checkbox.activeBorder,
                            },
                            "&.Mui-checked svg": {
                                fill: designtoken.checkbox.colorChecked,
                            },
                            "&.Mui-disabled.Mui-checkend svg": {},
                            "&:hover.Mui-checked svg": {
                                fill: designtoken.checkbox.hoverAndChecked,
                            },
                            "& .MuiButtonBase": {
                                padding: 0,
                            },
                        },
                    },
                },

                MuiSlider: {
                    defaultProps: {
                        fullWidth: true,
                    },
                    styleOverrides: {
                        root: {
                            marginTop: "8px",
                            color: designtoken.slider.color,
                            "& .MuiSlider-track": {
                                border: "none",
                            },
                            "& .MuiSlider-thumb": {
                                height: 22,
                                width: 22,
                                backgroundColor: "#fff",
                                border: "4px solid currentColor",
                                "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible":
                                    {
                                        boxShadow: "inherit",
                                    },
                                "&:before": {
                                    display: "block",
                                },
                            },
                            "& .MuiSlider-valueLabel": {
                                display: "none",
                            },
                        },
                    },
                },
                MuiChip: {
                    variants: [
                        {
                            props: {color: "success"},
                            style: {
                                backgroundColor:
                                designtoken.chip.success.backgroundColor,
                                color: "#000",
                                maxHeight: "24px",
                                "& .MuiChip-avatar": {
                                    padding: "8px",
                                    backgroundColor:
                                    designtoken.chip.success
                                        .avatarBackgroundColor,
                                },
                            },
                        },
                        {
                            props: {color: "error"},
                            style: {
                                backgroundColor:
                                designtoken.chip.error.backgroundColor,
                                color: "#000",
                                maxHeight: "24px",
                                "& .MuiChip-avatar": {
                                    padding: "8px",
                                    backgroundColor:
                                    designtoken.chip.error
                                        .avatarBackgroundColor,
                                },
                            },
                        },
                        {
                            props: {color: "warning"},
                            style: {
                                backgroundColor:
                                designtoken.chip.warning.backgroundColor,
                                color: "#000",
                                maxHeight: "24px",
                                "& .MuiChip-avatar": {
                                    padding: "8px",
                                    backgroundColor:
                                    designtoken.chip.warning
                                        .avatarBackgroundColor,
                                },
                            },
                        },
                        {
                            props: {color: "info"},
                            style: {
                                backgroundColor:
                                designtoken.chip.info.backgroundColor,
                                color: "#000",
                                maxHeight: "24px",
                                "& .MuiChip-avatar": {
                                    padding: "8px",
                                    backgroundColor:
                                    designtoken.chip.info
                                        .avatarBackgroundColor,
                                },
                            },
                        },
                        {
                            props: {color: "neutral"},
                            style: {
                                backgroundColor:
                                designtoken.chip.neutral.backgroundColor,
                                color: "#000",
                                maxHeight: "24px",
                                "& .MuiChip-avatar": {
                                    padding: "8px",
                                    backgroundColor:
                                    designtoken.chip.neutral
                                        .avatarBackgroundColor,
                                },
                            },
                        },
                    ],
                    styleOverrides: {
                        root: ({ownerState}) => {
                            const hasLabel = Boolean(ownerState.label)

                            return {
                                fontSize: fontSize["200"],
                                fontWeight: fontWeight.semibold,
                                "& .MuiChip-avatar": {
                                    width: "12px",
                                    height: "12px",
                                    marginLeft: "6px",
                                    marginRight: hasLabel ? 0 : "6px",
                                },
                                "& .MuiChip-label": {
                                    paddingLeft: hasLabel
                                        ? ownerState.hasIcon
                                            ? 0
                                            : "8px"
                                        : 0,
                                    paddingRight: hasLabel ? "8px" : 0,
                                },
                                "& .MuiChip-avatar + .MuiChip-label": {
                                    paddingLeft: hasLabel ? "6px" : 0,
                                },
                            }
                        },
                    },
                },
                MuiSwitch: {
                    styleOverrides: {
                        root: {
                            marginRight: 0,
                            width: "54px",
                            height: "32px",
                            padding: 0,
                            display: "flex",
                            "& .MuiSwitch-thumb": {
                                width: "24px",
                                height: "24px",
                                borderRadius: 40,
                            },
                            "& .MuiSwitch-track": {
                                borderRadius: 40,
                                opacity: 1,
                                backgroundColor: designtoken.switch.default.trackColor,
                                boxSizing: "border-box",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: designtoken.switch.default.trackColorChecked,
                                opacity: 1,
                            },
                            "& .MuiSwitch-switchBase": {
                                padding: 4,
                                "&.Mui-checked": {
                                    color: designtoken.switch.checked.thumbColor,
                                },
                                "&.Mui-disabled": {
                                    "& + .MuiSwitch-track": {
                                        opacity: 1,
                                        backgroundColor: designtoken.switch.disabled.trackColor,
                                    },
                                    "& > *:not(input)": {
                                        backgroundColor: designtoken.switch.disabled.thumbBackgroundColor,
                                        border: `1px solid ${designtoken.switch.disabled.thumbBorderColor}`,
                                    },
                                    "& svg": {
                                        color: designtoken.switch.disabled.iconColor,
                                    },
                                },
                            },
                        },
                    },
                },
                MuiPickersDay: {
                    styleOverrides: {
                        root: {
                            "&.Mui-selected": {
                                backgroundColor: designtoken.datepicker.selected.backgroundColor,
                                color: designtoken.datepicker.selected.color,
                                "&:hover, &:focus": {
                                    backgroundColor: designtoken.datepicker.selected.hoverBackgroundColor,
                                },
                            },
                        },
                    },
                },
                MuiAccordion: {
                    styleOverrides: {
                        root: {
                            marginBottom: 16,
                            "&:not(:last-of-type)": {
                                borderBottom: 0,
                            },
                            "&::before": {
                                display: "none",
                            },
                            "&:last-of-type": {
                                marginBottom: 0,
                            },
                        },
                    },
                },
                MuiAccordionSummary: {
                    styleOverrides: {
                        root: {
                            minHeight: "57px",
                            height: "57px",
                            "&.Mui-expanded": {
                                minHeight: "57px",
                            },
                        },
                    },
                },
                MuiAccordionDetails: {
                    styleOverrides: {
                        root: {
                            padding: "16px",
                        },
                    },
                },
                MuiLink: {
                    styleOverrides: {
                        root: () => ({
                            color: designtoken.link.default.color,
                            textDecoration: "underline",
                            transition: "color 0.3s ease",
                            "&:hover": {
                                color: designtoken.link.default.hoverColor,
                                textDecoration: "underline",
                            },
                        }),
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        paper: {
                            borderRadius: 16,
                            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.5)",
                            maxWidth: "1000px",
                            padding: "8px"
                        },
                    },
                },
                MuiDialogTitle: {
                    styleOverrides: {
                        root: {
                            paddingBottom: 0,
                            marginBottom: "8px",
                        },
                    },
                },
                MuiDialogContent: {
                    styleOverrides: {
                        root: {
                            padding: "8px",
                        },
                    },
                },
                MuiPaginationItem: {
                    styleOverrides: {
                        root: {
                            minWidth: "16px",
                            height: "16px",
                            padding: "0",
                            "&.Mui-selected": {
                                backgroundColor:
                                designtoken.paginationItem.active
                                    .backgroundColor,
                                "&:hover": {
                                    backgroundColor:
                                    designtoken.paginationItem.active
                                        .backgroundColor,
                                },
                            },
                            backgroundColor:
                            designtoken.paginationItem.default
                                .backgroundColor,
                        },
                    },
                },
            },
            Thumb: {
                backgroundColor: "#fff",
                width: 23,
                height: 23,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& svg": {
                    width: 15,
                    height: 15,
                },
            },
            ZIELSCHEIBE: {
                ring1: designtoken.chartColor.first,
                ring2: designtoken.chartColor.second,
                ring3: designtoken.chartColor.third,
                trackOpacity: 0.15,
            },
            Schiessplatz: {
                barChart: {
                    primary: designtoken.schiessplatz.barChart.default.primary,
                    secondary:
                    designtoken.schiessplatz.barChart.default.secondary,
                    tertiary:
                    designtoken.schiessplatz.barChart.default.tertiary,
                },
                donut: {
                    primary: designtoken.schiessplatz.donut.default.primary,
                    secondary: designtoken.schiessplatz.donut.default.secondary,
                    tertiary:
                    designtoken.schiessplatz.donut.default.tertiary,
                },
            },
            AvRiester: {
                primary: designtoken.chart.primary,
                secondary: designtoken.chart.secondary,
                tertiary: designtoken.chart.tertiary,
                quartary: designtoken.chart.quartary,
                quintary: designtoken.chart.quintary,
            },
            Card: {
                border: `1px ${designtoken.card.borderColor} solid`,
                padding: "15px 15px 0px 15px",
                borderRadius: "16px",
            },
            Box: {
                BoxContainer: {
                    borderRadius: "16px",
                    paddingLeft: "16px",
                    backgroundColor: designtoken.box.backgroundColor,
                    height: "100%",
                    padding: "24px 32px",
                },
                BoxTitle: {
                    display: "none",
                },
                BoxContent: {},
                OuterWrapper: {},
                Wrapper: {
                    borderRadius: designtoken.box.narrow.borderRadius,
                    padding: designtoken.box.narrow.padding,
                    ...containerQuery(400, {
                        borderRadius: designtoken.box.wide.borderRadius,
                        padding: designtoken.box.wide.padding,
                    }),
                },
                DEFAULT: {},
                GREY: {
                    backgroundColor: designtoken.box.grey.backgroundColor,
                },
                SHADOW: {
                    backgroundColor: designtoken.box.shadow.backgroundColor,
                    boxShadow: designtoken.box.shadow.boxShadowNarrow,
                    border: "1px solid #EEE",
                    ...containerQuery(400, {
                        boxShadow: designtoken.box.shadow.boxShadow,
                    }),
                },
            },
            GRID: {
                gap: "24px",
                columnGap: "24px",
                gridAutoRows: "auto",
                alignItems: "center",
            },
            DROPAREABOX: {
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: "10px",
                height: "100%",
                backgroundColor: "#c3c3c3",
            },
            DRAGGABLE: {
                backgroundColor: "#c3c3c3",
                width: "70px",
                height: "70px",
                borderRadius: "10%",
                padding: "5px",
            },
            DRAGABLE_BUTTON: {
                backgroundColor: "#072ECA",
                color: "#fff",
            },
            PRODUKT_SUCHE_TRASH_ICON: {
                color: designtoken.produktSucheEntryTrashIcon.color,
            },
            ICON_BUTTON_OUTLINED: {
                padding: designtoken.iconButtonOutlined.padding,
                borderRadius: designtoken.iconButtonOutlined.borderRadius,
                border: designtoken.iconButtonOutlined.border,
                "&.Mui-disabled": {
                    backgroundColor:
                    designtoken.iconButtonOutlined.disabledBackgroundColor,
                    border: designtoken.iconButtonOutlined.disabledBorder,
                },
                primary: {
                    backgroundColor: designtoken.button.primary.backgroundColor,
                    color: designtoken.button.primary.color,
                    border: "none",
                    "&:hover": {
                        backgroundColor:
                            designtoken.button.primary.backgroundColorHover
                            ?? designtoken.button.primary.backgroundColorHover,
                        color: designtoken.button.primary.color,
                    },
                    "&.Mui-disabled": {
                        backgroundColor:
                        designtoken.button.secondary.backgroundDisabled,
                        color: designtoken.button.secondary.colorDisabled,
                        border: "none",
                    },
                },
                secondary: {
                    backgroundColor:
                    designtoken.button.secondary.backgroundColor,
                    color: designtoken.button.secondary.color,
                    border: "none",
                    "&:hover": {
                        backgroundColor:
                        designtoken.button.secondary.backgroundColorHover,
                        color: designtoken.button.secondary.colorHover,
                    },
                    "&.Mui-disabled": {
                        backgroundColor:
                        designtoken.button.secondary.backgroundDisabled,
                        color: designtoken.button.secondary.colorDisabled,
                        border: "none",
                    },
                },
                tertiary: {
                    backgroundColor:
                    designtoken.button.tertiary.backgroundColor,
                    color: designtoken.button.tertiary.color,
                    border: `1px solid ${designtoken.button.tertiary.borderColor}`,
                    "&:hover": {
                        backgroundColor:
                        designtoken.button.tertiary.backgroundColorHover,
                        color: designtoken.button.tertiary.colorHover,
                    },
                    "&.Mui-disabled": {
                        backgroundColor:
                        designtoken.button.secondary.backgroundDisabled,
                        color: designtoken.button.secondary.colorDisabled,
                        border: designtoken.iconButtonOutlined.disabledBorder,
                    },
                },
            },
            Switch: {
                Thumb: {
                    backgroundColor:
                    designtoken.switch.default.thumbBackgroundColor,
                    border: `1px solid ${designtoken.switch.default.thumbBorderColor}`,
                    width: 23,
                    height: 23,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "& svg": {
                        width: 15,
                        height: 15,
                        color: designtoken.switch.default.iconColor,
                    },
                },
                Label: {
                    textAlign: "left",
                    color: designtoken.switch.default.labelColor,
                },
                Wrapper: {},
            },
            SEGMENTBUTTON: {
                padding: "2px 16px",
                height: "32px",
                textTransform: "none",
                borderRadius: designtoken.segmentbutton.borderRadius,
                color: designtoken.segmentbutton.color,
                fontWeight: "500",
                fontStyle: "normal",
                fontSize: "14px",
                whiteSpace: "nowrap",
                "&:hover": {
                    color: designtoken.segmentbutton.colorHover,
                    backgroundColor:
                    designtoken.segmentbutton.backgroundColorHover,
                },
                "&.active": {
                    backgroundColor: designtoken.segmentbutton.backgroundColor,
                    color: designtoken.segmentbutton.colorActive,
                    "&:hover": {
                        color: designtoken.segmentbutton.colorActive,
                    },
                },
                "&.active.Mui-disabled": {
                    backgroundColor:
                    designtoken.segmentbutton.disabledBackgroundColor,
                    color: "#000",
                },
                container: {
                    backgroundColor:
                    designtoken.segmentbutton.containerBackgroundColor,
                    display: "flex",
                    padding: "4px",
                    border: `1px solid ${designtoken.segmentbutton.borderColor}`,
                    width: "fit-content",
                    borderRadius:
                    designtoken.segmentbutton.containerBorderRadius,
                    gap: "8px",
                },
            },
            PAGE: {
                box: {
                    padding: designtoken.page.default.boxPadding,
                    borderRadius: designtoken.page.default.boxBorderRadius,
                },
            },
            DIALOG_WRAPPER: {
                "& .MuiPaper-root": {
                    padding: "20px",
                },
            },
            DROPDOWNBOX: {
                cursor: "pointer",
                backgroundColor: designtoken.inputField.default.backgroundColor,
                borderRadius: designtoken.dropdownbox.borderRadius,
                border: "1px solid",
                borderColor: designtoken.dropdownbox.borderColor,
                padding: "8px 12px",
                display: "flex",
                justifyContent: "space-between",
                "&: hover": {
                    borderColor: designtoken.dropdownbox.borderColorHover,
                },
            },
            STYLED_LABEL: {
                color: designtoken.label.color,
                colorDisabled: designtoken.label.colorDisabled,
            },
            RADIOBUTTONGROUP: {
                padding: "0px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                border: "1px solid",
                borderColor: designtoken.radiobuttongroup.borderColor,
                borderColorActive:
                designtoken.radiobuttongroup.borderColorActive,
                borderRadius: designtoken.radiobuttongroup.borderRadius,
                cursor: "pointer",

                "& svg": {
                    width: "24px",
                    height: "24px",
                    fill: designtoken.radiobuttongroup.borderColor,
                },
                "&:hover svg": {
                    width: "24px",
                    height: "24px",
                    fill: designtoken.radiobuttongroup.hoverBorderColor,
                },
                "&:hover": {
                    borderColor: designtoken.radiobuttongroup.hoverBorderColor,
                },
                "&.Mui-focused": {
                    boxShadow: designtoken.radiobuttongroup.focusedBoxShadow,
                    borderRadius: designtoken.radiobuttongroup.borderRadius,
                    borderWidth:
                    designtoken.radiobuttongroup.focusedBorderWidth,
                    borderColor:
                    designtoken.radiobuttongroup.focusedBorderColor,
                },
                "&.Mui-disabled": {
                    borderColor:
                    designtoken.radiobuttongroup.disabledBorderColor,
                    backgroundColor:
                    designtoken.radiobuttongroup.disabledBackgroundColor,
                },
            },
            PDFCARD: {
                imagebox: {
                    maxWidth: "120px",
                    maxHeight: "109px",
                    overflow: "hidden",
                    ...designtoken.weitereUnterlagenImageBox,
                },
                maintext: {
                    ...designtoken.weitereUnterlagenMainText,
                },
                subtext: {
                    ...designtoken.WeitereUnterlagenSubText,
                },
                wrapper: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    ...designtoken.unterlageCardWrapper,
                },
            },
            INFOBUTTON: {
                color: designtoken.infoButton.color,
            },
            BARCOLORS: {
                first: designtoken.barcolors.first,
                second: designtoken.barcolors.second,
            },
            NAVBAR: {
                navBarListItem: (theme, isActive) => ({
                    minHeight: 48,
                    minWidth: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: designtoken.navbar.default.borderRadius,
                    border: "1px solid",
                    borderColor: designtoken.navbar.default.borderColor,
                    boxShadow:
                        "0 3px 1px 0 rgba(97, 97, 97, 0.00), 0 2px 1px 0 rgba(97, 97, 97, 0.01), 0 1px 1px 0 rgba(97, 97, 97, 0.05), 0 0 1px 0 rgba(97, 97, 97, 0.09)",
                    width: "48px",
                    height: "48px",
                    padding: "12px",
                    backgroundColor: isActive
                        ? designtoken.navbar.active.backgroundColor
                        : designtoken.navbar.default.backgroundColor,
                    cursor: "pointer",
                    "& path": {
                        stroke: isActive
                            ? designtoken.navbar.active.color
                            : designtoken.navbar.default.color,
                    },
                    "& .MuiSvgIcon-root": {
                        stroke: isActive
                            ? designtoken.navbar.active.color
                            : designtoken.navbar.default.color,
                    },
                    "&:hover": {
                        backgroundColor: isActive
                            ? designtoken.navbar.hoveractive.backgroundColor
                            : designtoken.navbar.hover.backgroundColor,
                        border: `1px solid ${designtoken.navbar.hover.borderColor}`,
                        "& .MuiSvgIcon-root": {
                            stroke: isActive
                                ? designtoken.navbar.hoveractive.color
                                : designtoken.navbar.hover.color,
                        },
                    },
                    "&:hover path": {
                        stroke: isActive
                            ? designtoken.navbar.hoveractive.color
                            : designtoken.navbar.hover.color,
                    },
                    "& span": {
                        color: isActive
                            ? designtoken.navbar.active.color
                            : designtoken.navbar.default.color,
                    },
                    "&:hover span": {
                        color: isActive
                            ? designtoken.navbar.hoveractive.color
                            : designtoken.navbar.hover.color,
                    },
                }),
            },
            NOTIFICATIONCENTER: {
                qButton: (theme) => {
                    const base = theme.NAVBAR.navBarListItem(theme, false)
                    return {
                        ...base,
                        width: "48px",
                        height: "36px",
                        minWidth: "48px",
                        minHeight: "36px",
                        padding: "6px",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: designtoken.navbar.default.color,
                        "&:hover": {
                            ...base["&:hover"],
                            color: designtoken.navbar.hover.color,
                        },
                    }
                },
                wrapper: {
                    position: "absolute",
                    top: "22px",
                    right: "16px",
                    zIndex: 20,
                },
                menuPaper: {
                    width: 360,
                    maxHeight: 480,
                },
                header: {
                    px: 2,
                    py: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0.5,
                },
                emptyState: {
                    px: 2,
                    py: 3,
                },
                itemWrapper: {
                    px: 1.5,
                    py: 0.75,
                },
                toastCard: (color) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: color,
                    color: "#fff",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    boxShadow:
                        "0 1px 10px 0 rgba(0,0,0,0.10), 0 2px 15px 0 rgba(0,0,0,0.05)",
                }),
                toastMessage: {
                    flexGrow: 1,
                    wordBreak: "break-word",
                },
                closeButton: {
                    color: "#fff",
                    flexShrink: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.35)",
                    },
                },
                typeColors: {
                    info: "var(--toastify-color-info, #3498db)",
                    success: "var(--toastify-color-success, #07bc0c)",
                    warning: "var(--toastify-color-warning, #f1c40f)",
                    error: "var(--toastify-color-error, hsl(6, 78%, 57%))",
                    default: "#727283",
                },
            },
            ICONCARD: {
                container: {
                    color: designtoken.iconcard.default.fontColor,
                    backgroundColor:
                    designtoken.iconcard.default.backgroundColor,
                    borderRadius: designtoken.iconcard.default.borderRadius,
                    height: "180px",
                    padding: "5px",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-around",
                    cursor: "pointer",
                },
                label: {
                    color: designtoken.iconcard.default.labelColor,
                    fontWeight: "600",
                },
            },
            WELTBOARD: {
                ItemVisual: {
                    position: "absolute",
                    padding: 0.5,
                    backgroundColor:
                    designtoken.weltboard.default.itemVisualBackgroundColor,
                    borderRadius:
                    designtoken.weltboard.default.itemVisualBorderRadius,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 100,
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                    cursor: "pointer",
                    borderColor:
                    designtoken.weltboard.default.itemVisualBorderColor,
                    boxSizing: "border-box",
                    transition: "border 0.2s ease-in-out",
                },
                LayerContainer: {
                    height: "80vh",
                    width: "100%",
                    border: "1px solid #ddd",
                },
                SelectArea: {
                    padding: 2,
                    borderBottom: "1px solid #eee",
                    backgroundColor:
                    designtoken.weltboard.default.selectAreaBackgroundColor,
                },
                BoardArea: {
                    flexGrow: 1,
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor:
                    designtoken.weltboard.default.bordAreaBackgroundColor,
                    padding: 2,
                },
                ItemWrapper: {
                    width: "100%",
                    height: "100%",
                    backgroundColor:
                    designtoken.weltboard.default
                        .itemWrapperBackgroundColor,
                    position: "relative",
                    borderRadius: 4,
                    boxShadow: "inset 0 0 10px rgba(0,0,0,0.05)",
                    overflow: "auto",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "100%",
                },
            },
            HEROUNIT: {
                container: {
                    backgroundColor:
                    designtoken.herounit.default.backgroundColor,
                    borderRadius: "8px",
                    padding: "30px",
                    height: "100%",
                    minHeight: "180px",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                },
                label: {
                    color: designtoken.herounit.default.backgroundColor
                        .fontColor,
                    fontWeight: "600",
                    marginBottom: "20px",
                    maxWidth: "80%",
                },
                button: {
                    width: "30%",
                },
            },
            FRAGEKATALOG: {
                QuestionMulti: {
                    Header: {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "24px",
                    },
                    ActionButton: {
                        borderRadius:
                        designtoken.fragenkatalog.default
                            .questionMultiActionButtonBorderRadius,
                        border: `1px solid ${designtoken.fragenkatalog.default.questionMultiActionButtonBorderColor}`,
                    },
                    ButtonGrid: {
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        marginTop: "24px",
                    },
                    NavButton: (active) => ({
                        minWidth: "45px",
                        height: "45px",
                        borderRadius:
                        designtoken.fragenkatalog.default
                            .questionMultiNavButtonBorderRadius,
                        border: `1px solid ${designtoken.fragenkatalog.default.questionMultiNavButtonBorderColor}`,
                        backgroundColor: active
                            ? designtoken.fragenkatalog.active
                                .questionMultiNavButtonBackgroundColor
                            : designtoken.fragenkatalog.default
                                .questionMultiNavButtonBackgroundColor,
                        color: active
                            ? designtoken.fragenkatalog.active
                                .questionMultiNavButtonColor
                            : designtoken.fragenkatalog.default
                                .questionMultiNavButtonColor,
                        "&:hover": {
                            backgroundColor: active
                                ? designtoken?.fragenkatalog?.hoveractive
                                    ?.questionMultiNavButtonBackgroundColor
                                : designtoken?.fragenkatalog?.hover
                                    ?.questionMultiNavButtonBackgroundColor,
                        },
                    }),
                    BORDERLESS: {
                        Row: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        },
                        Title: {
                            color: designtoken.fragenkatalog.borderless.titleColor,
                            fontFamily:
                            designtoken.fragenkatalog.borderless.titleFontFamily,
                            fontSize:
                            designtoken.fragenkatalog.borderless.titleFontSize,
                            fontWeight:
                            designtoken.fragenkatalog.borderless.titleFontWeight,
                            lineHeight:
                            designtoken.fragenkatalog.borderless.titleLineHeight,
                        },
                        AddButton: {
                            borderRadius:
                            designtoken.fragenkatalog.borderless
                                .addButtonBorderRadius,
                            borderColor:
                            designtoken.fragenkatalog.borderless
                                .addButtonBorderColor,
                            color: designtoken.fragenkatalog.borderless
                                .addButtonColor,
                            textTransform: "none",
                        },
                        DetailArea: {
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            paddingLeft: "16px",
                            paddingTop: "8px",
                            paddingBottom: "16px",
                        },
                        IconButton: {
                            color: designtoken.fragenkatalog.borderless
                                .iconButtonColor,
                            borderRadius:
                            designtoken.fragenkatalog.borderless
                                .iconButtonBorderRadius,
                            border: `1px solid ${designtoken.fragenkatalog.borderless.iconButtonBorderColor}`,
                            padding: "6px",
                        },
                        IndexBadge: (active) => ({
                            minWidth: "32px",
                            height: "32px",
                            padding: 0,
                            borderRadius:
                            designtoken.fragenkatalog.borderless
                                .indexBadgeBorderRadius,
                            border: `1px solid ${designtoken.fragenkatalog.borderless.indexBadgeBorderColor}`,
                            backgroundColor: active
                                ? designtoken.fragenkatalog.borderless
                                    .indexBadgeActiveBackgroundColor
                                : designtoken.fragenkatalog.borderless
                                    .indexBadgeBackgroundColor,
                            color: active
                                ? designtoken.fragenkatalog.borderless
                                    .indexBadgeActiveColor
                                : designtoken.fragenkatalog.borderless
                                    .indexBadgeColor,
                        }),
                    },
                },
            },
            TABPROGRESSHEADER: {
                backButton: {
                    padding: "4px",
                },
                progressSegmentDefault: {
                    flex: 1,
                    height: designtoken.tabProgressHeader.default.height,
                    borderRadius:
                    designtoken.tabProgressHeader.default.borderRadius,
                    backgroundColor:
                    designtoken.tabProgressHeader.default.backgroundColor,
                    transition: "background-color 0.3s ease",
                },
                progressSegmentActive: {
                    flex: 1,
                    height: designtoken.tabProgressHeader.default.height,
                    borderRadius:
                    designtoken.tabProgressHeader.default.borderRadius,
                    backgroundColor:
                    designtoken.tabProgressHeader.active.backgroundColor,
                    transition: "background-color 0.3s ease",
                },
            },
            CARD: {
                title: {
                    ...designtoken?.card?.title?.default,
                },
                subtitle: {
                    ...designtoken?.card?.subtitle?.default,
                },
            },
            ACCORDION: {
                groupBorderRadius: designtoken.accordion.wrapper.groupBorderRadius,
                groupDividerColor: designtoken.accordion.wrapper.groupDividerColor,
                groupDividerHoverColor: designtoken.accordion.wrapper.groupDividerHoverColor,
                OUTLINED: {
                    hoverOutline: designtoken.accordion.wrapper.outlined.hoverOutline,
                    wrapper: {
                        outline: designtoken.accordion.wrapper.outlined.outline,
                        backgroundColor: designtoken.accordion.wrapper.outlined.backgroundColor,
                        minWidth: 0,
                        overflow: "hidden",
                        "&.MuiPaper-rounded.MuiAccordion-root": {
                            borderRadius: designtoken.accordion.wrapper.outlined.borderRadius,
                        },
                        "& .MuiAccordionSummary-root.Mui-disabled": {
                            opacity: 1,
                        },
                        "&.Mui-disabled": {
                            backgroundColor: designtoken.accordion.wrapper.outlined.backgroundColor,
                            outline: designtoken.accordion.wrapper.outlined.disabledOutline,
                            "&:hover": {
                                outline: designtoken.accordion.wrapper.outlined.disabledOutlineHover,
                            },
                            "& .accordionTitle, .accordionSubTitle, .MuiSvgIcon-root": {
                                color: designtoken.accordion.wrapper.outlined.titleColor,
                            },
                        },
                        "& .MuiAccordionSummary-root": {
                            padding: "16px",
                            minHeight: "auto",
                            height: "auto",
                            "&.Mui-expanded": {
                                minHeight: "auto",
                            },
                        },
                        "& .MuiAccordionSummary-content": {
                            margin: 0,
                            "&.Mui-expanded": {
                                minHeight: "auto",
                                margin: 0,
                            },
                        },
                        "& .MuiAccordionDetails-root": {
                            padding: "16px",
                        },
                        "&:hover, &:focus-within": {
                            outline: designtoken.accordion.wrapper.outlined.hoverOutline,
                        },
                        "&:active": {
                            outline: designtoken.accordion.wrapper.outlined.activeOutline,
                        },
                        "&.sizeSmall .MuiAccordionSummary-root": {
                            padding: "4px 16px",
                        },
                    },
                    title: {
                        ...designtoken.accordion.title,
                    },
                    subtitle: {
                        ...designtoken.accordion.subtitle,
                    },
                },
                DEFAULT: {
                    wrapper: {
                        backgroundColor: designtoken.accordion.wrapper.default.backgroundColor,
                        borderLeft: `${designtoken.accordion.wrapper.default.borderLeftWidth} solid ${designtoken.accordion.wrapper.default.borderLeftColor}`,
                        border: `1px solid ${designtoken.accordion.wrapper.default.borderColor}`,
                        borderLeftWidth: designtoken.accordion.wrapper.default.borderLeftWidth,
                        borderLeftColor: designtoken.accordion.wrapper.default.borderLeftColor,
                        boxShadow: designtoken.accordion.wrapper.default.boxShadow,
                        "&.MuiPaper-rounded.MuiAccordion-root": {
                            borderRadius: designtoken.accordion.wrapper.default.borderRadius,
                        },
                        "& .MuiAccordionSummary-root.Mui-disabled": {
                            opacity: 1,
                        },
                        "&.Mui-disabled": {
                            backgroundColor: designtoken.accordion.wrapper.default.backgroundColor,
                            borderLeftColor: designtoken.accordion.wrapper.default.disabledBorderLeftColor,
                            "& .accordionTitle, .accordionSubTitle, .MuiSvgIcon-root": {
                                color: designtoken.accordion.wrapper.default.disabledTitleColor,
                            },
                        },
                        "& .MuiAccordionSummary-root": {
                            padding: "16px",
                            minHeight: "auto",
                            height: "auto",
                            "&.Mui-expanded": {
                                minHeight: "auto",
                            },
                        },
                        "& .MuiAccordionSummary-content": {
                            margin: 0,
                        },
                        "& .MuiAccordionDetails-root": {
                            padding: "16px",
                        },
                        "& .MuiDivider-root": {
                            borderColor: designtoken.accordion.wrapper.default.dividerColor,
                        },
                    },
                    title: {
                        ...designtoken.accordion.title,
                    },
                    subtitle: {
                        ...designtoken.accordion.subtitle,
                    },
                },
            },
            ACCORDIONGROUP: {
                wrapper: (theme, variant) => ({
                    ...theme.ACCORDION?.[variant]?.wrapper,
                    "&:before": {display: "none"},
                    boxShadow: "none",
                    marginBottom: 0,
                    borderBottom: `1px solid ${theme.ACCORDION?.groupDividerColor}`,
                    position: "relative",
                    "&:hover, &:focus-within": {
                        outline: theme.ACCORDION?.[variant]?.hoverOutline,
                        borderBottomColor: theme.ACCORDION?.groupDividerHoverColor,
                        zIndex: 5,
                    },
                    "&.MuiPaper-rounded.MuiAccordion-root": {
                        borderRadius: 0,
                    },
                    "&.MuiPaper-rounded.MuiAccordion-root:first-of-type": {
                        borderTopLeftRadius: theme.ACCORDION?.groupBorderRadius,
                        borderTopRightRadius: theme.ACCORDION?.groupBorderRadius,
                    },
                    "&.MuiPaper-rounded.MuiAccordion-root:last-of-type": {
                        borderBottomLeftRadius: theme.ACCORDION?.groupBorderRadius,
                        borderBottomRightRadius: theme.ACCORDION?.groupBorderRadius,
                        borderBottom: "none",
                    },
                }),
                title: (theme, variant) => ({
                    ...theme.ACCORDION?.[variant]?.title,
                }),
                subtitle: (theme, variant) => ({
                    ...theme.ACCORDION?.[variant]?.subtitle,
                }),
            },
            VERSORGUNGSLUECKENVERLAUF: {
                luecke: designtoken?.versorgungsverlauf?.default?.firstLayer,
                einkuenfte: designtoken?.versorgungsverlauf?.default?.thirdLayer,
                bestehendeVersorgung: designtoken?.versorgungsverlauf?.default?.fourthLayer,
                rente: designtoken?.versorgungsverlauf?.default?.secondLayer,
                lueckePartner: designtoken?.versorgungsverlauf?.default?.fifthLayer,
                neueVersorgung: designtoken?.versorgungsverlauf?.default?.sixthLayer,
                summe: designtoken?.versorgungsverlauf?.default?.seventhLayer,
            },
            BUTTONSELECTWIDGET: {
                child: (theme, isSelected) => ({
                    padding: "10px",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: isSelected
                        ? designtoken?.buttonselect?.childBorderColor
                        : designtoken?.buttonselect?.unselectedColor,
                    borderRadius: "4px",
                    display: "flex",
                    outline: isSelected
                        ? `1px solid ${designtoken?.buttonselect?.childBorderColor}`
                        : "none",
                    cursor: "pointer",
                    "& > div": {
                        boxShadow: isSelected
                            ? `inset 0 0 0 3px white, inset 0 0 0 12px ${designtoken?.buttonselect?.childBorderColor}`
                            : "none",
                    },
                    "&:hover": {
                        outline: `1px solid ${designtoken?.buttonselect?.childBorderColor}`,
                        "& > div": {
                            borderColor:
                            designtoken?.buttonselect?.childBorderColor,
                            borderWidth: "2px",
                        },
                    },
                }),
                icon: (theme, isSelected) => ({
                    height: "24px",
                    width: "24px",
                    minHeight: "24px",
                    minWidth: "24px",
                    borderWidth: isSelected ? "2px" : "1px",
                    borderStyle: "solid",
                    borderColor: isSelected
                        ? designtoken?.buttonselect?.iconBorderColor
                        : designtoken?.buttonselect?.unselectedColor,
                    borderRadius: "50%",
                    boxSizing: "border-box",
                    display: "inline-block",
                    marginRight: "16px",
                }),
            },
            LEBENSBAUSTEINBUTTON: {
                backgroundColor:
                designtoken?.lebensbausteinbutton?.backgroundColor,
                boxShadow: "0px 4px 15px 0px rgba(0,0,0,0.20)",
                height: "80px",
                width: "80px",
                minWidth: "80px",
                padding: 0,
                textTransform: "none",
                "&:hover": {
                    backgroundColor:
                    designtoken?.lebensbausteinbutton?.backgroundColorHover,
                },
            },
            STYLEDBOX: {
                selectedBorderColor: designtoken.styledbox.selectedBorderColor,
            },
            STYLEDTABLE: {
                headerBackground: designtoken.styledTable.headerBackground,
                dividerColor: designtoken.styledTable.dividerColor,
                borderColor: designtoken.styledTable.borderColor,
            },
            ALTERSVORSORGETABLE: {
                primaryColor: designtoken.altersvorsorgeTable.primaryColor,
            },
            LABEL: {
                errorColor: designtoken.label.errorColor,
            },
            LINKWIDGET: {
                errorColor: designtoken.link.default.errorColor,
            },
            VERTRAGSWIDGET: {
                avatarBackgroundColor:
                designtoken.vertragsWidget.avatarBackgroundColor,
            },
            HONEYWIDGET: {
                disabledColor: designtoken.honeyWidget.disabledColor,
                borderStrokeColor: designtoken.honeyWidget.borderStrokeColor,
            },
            TABITEM: {
                selectedBackgroundColor:
                designtoken.tabItem.selectedBackgroundColor,
                selectedColor: designtoken.tabItem.selectedColor,
            },
            APP: {
                backgroundColor: designtoken.background.paper,
            },
            NAVITEM: {
                backgroundColor: designtoken.navItem.backgroundColor,
                hoverBackgroundColor: designtoken.navItem.hoverBackgroundColor,
            },
            HEADLINEBOX: {
                backgroundColor: designtoken.headlineBox.backgroundColor,
            },
            EVENTSANDBARCHART: {
                event: {
                    circleBackground: designtoken.eventsAndBarchart?.event?.circleBackground,
                    circleBorder: designtoken.eventsAndBarchart?.event?.circleBorder,
                    dashColor: designtoken.eventsAndBarchart?.event?.dashColor,
                },
                axis: {
                    lineColor: designtoken.eventsAndBarchart?.axis?.lineColor,
                    tickColor: designtoken.eventsAndBarchart?.axis?.tickColor,
                    yearTextColor: designtoken.eventsAndBarchart?.axis?.yearTextColor,
                    markerColor: designtoken.eventsAndBarchart?.axis?.markerColor,
                    markerTextColor: designtoken.eventsAndBarchart?.axis?.markerTextColor,
                },
                legend: {
                    textColor: designtoken.eventsAndBarchart?.legend?.textColor,
                },
            },
            VERTICALSLIDER: {
                root: {
                    width: `${designtoken.slider.trackWidth ?? 12}px`,
                    minHeight: "100px",
                    padding: "0 8px",
                    "& .MuiSlider-rail": {
                        width: `${designtoken.slider.trackWidth ?? 12}px`,
                        backgroundColor: designtoken.slider.railColor,
                        borderRadius: `${(designtoken.slider.trackWidth ?? 12) / 2}px`,
                        opacity: 1,
                    },
                    "& .MuiSlider-track": {
                        width: `${designtoken.slider.trackWidth ?? 12}px`,
                        backgroundColor: designtoken.slider.color,
                        border: "none",
                        borderRadius: `${(designtoken.slider.trackWidth ?? 12) / 2}px`,
                    },
                    "& .MuiSlider-thumb": {
                        width: `${designtoken.slider.thumbSize ?? 24}px`,
                        height: `${designtoken.slider.thumbSize ?? 24}px`,
                        backgroundColor: designtoken.slider.thumbBackgroundColor,
                        border: `2px solid ${designtoken.slider.thumbBorderColor}`,
                        boxShadow: "none",
                        "&:hover, &.Mui-focusVisible": {
                            boxShadow: `0 0 0 6px ${designtoken.slider.color}22`,
                        },
                        "&.Mui-active": {
                            boxShadow: `0 0 0 8px ${designtoken.slider.color}33`,
                        },
                    },
                },
            },
            HORIZONTALSLIDER: {
                root: {
                    height: `${designtoken.slider.trackWidth ?? 12}px`,
                    minWidth: "100px",
                    padding: "8px 0",
                    "& .MuiSlider-rail": {
                        height: `${designtoken.slider.trackWidth ?? 12}px`,
                        backgroundColor: designtoken.slider.railColor,
                        borderRadius: `${(designtoken.slider.trackWidth ?? 12) / 2}px`,
                        opacity: 1,
                    },
                    "& .MuiSlider-track": {
                        height: `${designtoken.slider.trackWidth ?? 12}px`,
                        backgroundColor: designtoken.slider.color,
                        border: "none",
                        borderRadius: `${(designtoken.slider.trackWidth ?? 12) / 2}px`,
                    },
                    "& .MuiSlider-thumb": {
                        width: `${designtoken.slider.thumbSize ?? 24}px`,
                        height: `${designtoken.slider.thumbSize ?? 24}px`,
                        backgroundColor: designtoken.slider.thumbBackgroundColor,
                        border: `2px solid ${designtoken.slider.thumbBorderColor}`,
                        boxShadow: "none",
                        "&:hover, &.Mui-focusVisible": {
                            boxShadow: `0 0 0 6px ${designtoken.slider.color}22`,
                        },
                        "&.Mui-active": {
                            boxShadow: `0 0 0 8px ${designtoken.slider.color}33`,
                        },
                    },
                },
            },
            TIMELINECOMPONENT: {
                BOX: {
                    width: "100%",
                    backgroundColor: designtoken?.timeline?.timelineBackground,
                },
                ICON: (theme, isThisYear) => ({
                    width: isThisYear ? "18px" : "10px",
                    height: isThisYear ? "18px" : "10px",
                    backgroundColor: designtoken?.timeline?.timelineIconColor,
                    borderRadius: "50%",
                }),
                INNERBOX: {
                    backgroundSize: "100%",
                    backgroundImage: "url(\"/grid.svg\")",
                    backgroundRepeat: "no-repeat",
                    borderBottom: `3px solid ${designtoken?.timeline?.timelineBottomColor}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    width: "100%",
                    height: "400px",
                },
            },
            AVRIESTERTABLE: {
                HEADER: {
                    backgroundColor: designtoken?.avriestertable?.header?.backgroundColor,
                },
            },
            WORKFLOWSTEPPER: {
                color: designtoken?.workflowstepper?.color,
            },
            ROOT_DIALOG: {
                title: {
                    ...designtoken?.dialog?.title,
                },
                subtitle: {
                    ...designtoken?.dialog?.subtitle,
                }
            }
        },
        deDE,
    )